import { useAuth0 } from '@auth0/auth0-react'
import { createStandaloneToast } from '@chakra-ui/react'
import axios from 'axios'
import { useEffect, useRef } from 'react'

import api from '@config/api'
import mrpApi from '@config/api-mrp'

import auth from '@service/auth'

// Erros do Auth0 que indicam sessão inválida (precisa logar novamente)
const AUTH_SESSION_ERRORS = [
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'missing_refresh_token'
]

// Toast standalone do Chakra: o app não monta <ToastContainer> do react-toastify
const toast = createStandaloneToast()

// Exibe o erro ao usuário (id evita toasts repetidos para N requests)
const showNotification = (
  message: string,
  status: 'error' | 'warning' = 'error'
) => {
  if (toast.isActive(message)) return
  toast({
    id: message,
    title: message,
    status,
    duration: 5000,
    isClosable: true,
    position: 'top-right'
  })
}

const notifyClientError = (status: number) => {
  if (status === 403) {
    showNotification(
      'Acesso negado. Você não tem permissão para esta operação.'
    )
  } else if (status === 429) {
    showNotification(
      'Muitas solicitações. Aguarde um momento e tente novamente.'
    )
  }
  // 400/404/422 e demais 4xx: cada tela trata (várias usam 404 como "sem dados")
}

const notifyNetworkError = (error: any) => {
  if (axios.isCancel(error)) return
  showNotification('Erro de conexão. Verifique sua internet e tente novamente.')
}

export const AxiosInterceptorProvider = ({ children }: any) => {
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE
  const tokenStorageKey = `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`

  const { user, getAccessTokenSilently, logout } = useAuth0()

  // Promise de refresh compartilhada: N requests com 401 simultâneos
  // aguardam o mesmo getAccessTokenSilently em vez de chamar N vezes
  const refreshPromiseRef = useRef<Promise<string> | null>(null)
  const loggingOutRef = useRef(false)

  const getStoredAccessToken = (): string | undefined => {
    try {
      const userInfos = JSON.parse(localStorage.getItem(tokenStorageKey))
      return userInfos?.body?.access_token
    } catch {
      return undefined
    }
  }

  const logoutOnce = () => {
    if (loggingOutRef.current) return
    loggingOutRef.current = true
    logout({ returnTo: window.location.origin })
  }

  const refreshAccessToken = () => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = getAccessTokenSilently({
        ignoreCache: true
      }).finally(() => {
        refreshPromiseRef.current = null
      })
    }
    return refreshPromiseRef.current
  }

  const retryWithNewToken = async (error: any) => {
    const originalRequest = error.config
    if (!originalRequest || originalRequest._retry) {
      return Promise.reject(error)
    }
    originalRequest._retry = true

    let newToken: string
    try {
      newToken = await refreshAccessToken()
    } catch (refreshError: any) {
      if (AUTH_SESSION_ERRORS.includes(refreshError?.error)) {
        logoutOnce()
      }
      return Promise.reject(error)
    }

    originalRequest.headers = originalRequest.headers || {}
    originalRequest.headers.Authorization = `Bearer ${newToken}`
    return axios(originalRequest)
  }

  const setAuthorizationHeader = async (req: any) => {
    if (req.headers) {
      req.headers.Authorization = `Bearer ${getStoredAccessToken() ?? ''}`
    }
    return req
  }

  const handleInterception = () => {
    const apiRequestId = api.interceptors.request.use(setAuthorizationHeader)

    const apiResponseId = api.interceptors.response.use(
      async response => response,
      async error => {
        if (!error.response) {
          // Erro de rede, servidor indisponível ou request cancelado
          notifyNetworkError(error)
          return Promise.reject(error)
        }

        const { config, response } = error
        const { status, data } = response

        if (status === 423) {
          const isAdminPage = window.location.pathname === '/admin/manutencao'
          if (
            !isAdminPage &&
            config?.url !== 'v1/Perfil' &&
            config?.url !== 'v1/ProcessoDiario/obterProcessoDiario' &&
            config?.url !== 'v1/Infra/Manutencao'
          ) {
            if (data?.Message === 'Jobs') {
              window.location.href = '/job'
            }
            if (data?.Message === 'Manutencao') {
              sessionStorage.setItem('manutencao_motivo', data.Motivo ?? '')
              sessionStorage.setItem('manutencao_previsao', data.Previsao ?? '')
              window.location.href = '/manutencao'
            }
          }
          return Promise.reject(error)
        }

        if (status === 401) {
          return retryWithNewToken(error)
        }

        if (status === 406) {
          localStorage.clear()

          await auth
            .getUserPopulateData()
            .then(async () => {
              logoutOnce()
            })
            .catch(() => {
              logoutOnce()
            })

          return Promise.reject(error)
        }

        if (status >= 400 && status < 500) {
          notifyClientError(status)
        }

        if (status >= 500 || status === null) {
          showNotification('Erro Interno do Servidor.', 'warning')
        }

        return Promise.reject(error)
      }
    )

    const mrpRequestId = mrpApi.interceptors.request.use(setAuthorizationHeader)

    const mrpResponseId = mrpApi.interceptors.response.use(
      async response => response,
      async error => {
        if (!error.response) {
          // Erro de rede, servidor indisponível ou request cancelado
          notifyNetworkError(error)
          return Promise.reject(error)
        }

        const { status } = error.response

        if (status === 401 || status === 406) {
          return retryWithNewToken(error)
        }

        if (status >= 400 && status < 500) {
          notifyClientError(status)
        }

        if (status >= 500 || status === null) {
          showNotification('Erro Interno do Servidor.', 'warning')
        }

        return Promise.reject(error)
      }
    )

    return () => {
      api.interceptors.request.eject(apiRequestId)
      api.interceptors.response.eject(apiResponseId)
      mrpApi.interceptors.request.eject(mrpRequestId)
      mrpApi.interceptors.response.eject(mrpResponseId)
    }
  }

  useEffect(() => {
    return handleInterception()
    // getAccessTokenSilently/logout vêm do Auth0 e são estáveis; os
    // interceptors só precisam ser recriados quando o usuário muda
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return <>{children}</>
}
